import React, { createContext, useEffect, useState } from "react";

export const ShopContext = createContext(null);

// Initialize default cart with 0 items
const getDefaultCart = () => {
  let cart = {};
  for (let index = 0; index <= 300; index++) {
    cart[index] = 0;
  }
  return cart;
};

const ShopContextProvider = ({ children }) => {
  const [allProducts, setAllProducts] = useState([]);
  const [cartItems, setCartItems] = useState(getDefaultCart());

  // Backend API base URL
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  useEffect(() => {
    // 🔒 ENV SAFETY (important for cPanel)
    if (!API_BASE_URL) {
      console.error("❌ REACT_APP_API_BASE_URL not defined");
      return;
    }

    // Fetch products
    const fetchProducts = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/products/all`);
        if (!res.ok) throw new Error("Failed to fetch products");
        const data = await res.json();
        setAllProducts(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("Error fetching products:", err);
      }
    };

    // Fetch cart
    const fetchCart = async () => {
      const token = localStorage.getItem("auth-token");
      if (!token) return;

      try {
        const res = await fetch(`${API_BASE_URL}/api/cart/get`, {
          headers: {
            "auth-token": token,
            "Content-Type": "application/json",
          },
        });
        if (!res.ok) throw new Error("Failed to fetch cart");
        const data = await res.json();
        setCartItems(data.cartItems || data || getDefaultCart());
      } catch (err) {
        console.error("Error fetching cart:", err);
      }
    };

    fetchProducts();
    fetchCart();
  }, [API_BASE_URL]);

  // Add to cart
  const addToCart = async (itemId) => {
    setCartItems((prev) => ({
      ...prev,
      [itemId]: (prev[itemId] || 0) + 1,
    }));

    const token = localStorage.getItem("auth-token");
    if (!token || !API_BASE_URL) return;

    try {
      await fetch(`${API_BASE_URL}/api/cart/add`, {
        method: "POST",
        headers: {
          "auth-token": token,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ itemId }),
      });
    } catch (err) {
      console.error("Add to cart error:", err);
    }
  };

  // Remove from cart
  const removeFromCart = async (itemId) => {
    setCartItems((prev) => ({
      ...prev,
      [itemId]: prev[itemId] > 0 ? prev[itemId] - 1 : 0,
    }));

    const token = localStorage.getItem("auth-token");
    if (!token || !API_BASE_URL) return;

    try {
      await fetch(`${API_BASE_URL}/api/cart/remove`, {
        method: "POST",
        headers: {
          "auth-token": token,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ itemId }),
      });
    } catch (err) {
      console.error("Remove from cart error:", err);
    }
  };

  // Total cart amount
  const getTotalCartAmount = () => {
    let total = 0;
    for (const item in cartItems) {
      if (cartItems[item] > 0) {
        const product = allProducts.find(
          (p) => p.id === Number(item)
        );
        if (!product) continue;
        total += (product.new_price || 0) * cartItems[item];
      }
    }
    return total;
  };

  // Total cart items
  const getTotalCartItems = () => {
    let total = 0;
    for (const item in cartItems) {
      if (cartItems[item] > 0) total += cartItems[item];
    }
    return total;
  };

  return (
    <ShopContext.Provider
      value={{
        allProducts,
        cartItems,
        addToCart,
        removeFromCart,
        getTotalCartAmount,
        getTotalCartItems,
      }}
    >
      {children}
    </ShopContext.Provider>
  );
};

export default ShopContextProvider;
